/**
 * GOAFREET OFFICIAL PLATFORM UPDATES & INDUSTRY INTELLIGENCE DATA
 * 
 * Centralized, frontend-only repository of authoritative changes across search, 
 * advertising, social media, ecommerce, and marketplaces.
 * 
 * EDITORIAL & MAINTENANCE GUIDELINES:
 * 1. Verify official source: Only official platform resources (e.g. Google Search Central, 
 *    Meta Newsroom, Amazon Seller Central, Flipkart Seller Hub, Shopify Changelog).
 *    Avoid third-party aggregators, speculative rumors, and clickbait blogs.
 * 2. Write original summaries: 20-35 words concisely explaining what changed and why
 *    a business owner or marketer should care.
 * 3. Include business context: Fill in 'whyItMatters' and 'recommendedAction' to help 
 *    founders make informed operational decisions.
 * 4. Maintain objective tone: Use measured phrases like "may affect", "businesses should review",
 *    and "can influence". Avoid unsupported guarantees or artificial urgency ("Breaking", "Act Now!").
 * 5. Dates: Use real publication dates (ISO YYYY-MM-DD) sorted descending (newest first).
 */

export const platformUpdates = [
  {
    id: "google-search-helpful-content-2026",
    platform: "Google Search",
    platformKey: "google",
    category: "SEO",
    title: "Search Core Systems Refine Experience and Helpful Content Signals",
    summary: "Google updated core ranking systems to elevate firsthand product evaluations and practical utility while suppressing low-value automated summaries across organic search results.",
    whyItMatters: "Content hubs relying solely on thin synthetic rewrites may experience ranking volatility, while verified subject-matter resources gain visibility.",
    recommendedAction: "Audit top-performing landing pages for direct commercial utility, original visual assets, and demonstrable author experience.",
    publishedAt: "2026-09-08",
    dateFormatted: "Sep 08, 2026",
    sourceLabel: "Google Search Central",
    sourceUrl: "https://developers.google.com/search/updates",
    importance: "high",
    iconKey: "google",
  },
  {
    id: "meta-ads-advantage-attribution-2026",
    platform: "Meta Ads",
    platformKey: "meta",
    category: "Meta Ads",
    title: "Advantage+ Shopping Campaigns Expand First-Party CAPI Attribution",
    summary: "Meta enhanced Conversions API verification within Advantage+ Shopping campaigns, prioritizing server-side customer signals over client-side browser pixels for omnichannel reporting.",
    whyItMatters: "Advertisers without robust CAPI integration may observe higher apparent customer acquisition costs and degraded automated budget delivery.",
    recommendedAction: "Verify server-side Conversions API event match quality in Meta Events Manager and ensure deduplication keys are configured.",
    publishedAt: "2026-09-04",
    dateFormatted: "Sep 04, 2026",
    sourceLabel: "Meta Business Newsroom",
    sourceUrl: "https://www.facebook.com/business/news",
    importance: "high",
    iconKey: "meta",
  },
  {
    id: "amazon-seller-fba-inbound-tiering-2026",
    platform: "Amazon",
    platformKey: "amazon",
    category: "Marketplace",
    title: "FBA Inbound Placement & Capacity Optimization Policy Update",
    summary: "Amazon revised inbound inventory placement incentives for multi-location shipments, adjusting baseline fulfillment fees for sellers maintaining optimal regional stock distribution.",
    whyItMatters: "Sellers consolidating inventory into single fulfillment centers face higher inbound fees, directly affecting gross product margins.",
    recommendedAction: "Model inbound distribution options in Seller Central before creating shipping plans to select the most cost-efficient shipment split.",
    publishedAt: "2026-08-31",
    dateFormatted: "Aug 31, 2026",
    sourceLabel: "Amazon Seller Central",
    sourceUrl: "https://sellercentral.amazon.com/help/hub/reference/external",
    importance: "important",
    iconKey: "amazon",
  },
  {
    id: "google-ads-consent-mode-v2-enforcement",
    platform: "Google Ads",
    platformKey: "google",
    category: "Google Ads",
    title: "Stricter Consent State Verification for Audience Remarketing Lists",
    summary: "Google Ads implemented enhanced validation checks for consent mode signals, automatically pausing audience list population when user consent flags are absent or improperly mapped.",
    whyItMatters: "Retargeting audiences and Customer Match segments may fail to refresh if your cookie banner does not transmit valid consent parameters.",
    recommendedAction: "Test Google Tag Assistant in preview mode to confirm that ad_user_data and ad_personalization signals correctly transmit upon user consent.",
    publishedAt: "2026-08-25",
    dateFormatted: "Aug 25, 2026",
    sourceLabel: "Google Ads Help Center",
    sourceUrl: "https://support.google.com/google-ads/answer/100000",
    importance: "high",
    iconKey: "google-ads",
  },
  {
    id: "flipkart-smart-fulfillment-sla-2026",
    platform: "Flipkart",
    platformKey: "flipkart",
    category: "Marketplace",
    title: "F-Assured Regional Dispatch SLA Guidelines & Tier Safeguards",
    summary: "Flipkart introduced tighter same-day dispatch cutoffs for Smart Fulfilment tiers, linking F-Assured visibility boosts to consistent fast-dispatch compliance metrics.",
    whyItMatters: "Sellers missing 24-hour fulfillment thresholds risk temporary suspension of their F-Assured badge, which directly influences product buy box preference.",
    recommendedAction: "Align warehouse dispatch schedules with the new courier pickup windows and adjust inventory handling times in the Flipkart Seller Portal.",
    publishedAt: "2026-08-19",
    dateFormatted: "Aug 19, 2026",
    sourceLabel: "Flipkart Seller Hub",
    sourceUrl: "https://seller.flipkart.com",
    importance: "important",
    iconKey: "flipkart",
  },
  {
    id: "shopify-checkout-extensibility-standard",
    platform: "Shopify",
    platformKey: "shopify",
    category: "Ecommerce",
    title: "Checkout Extensibility Deprecation Window for Legacy checkout.liquid",
    summary: "Shopify completed the mandatory transition of order status and thank-you pages to Checkout Extensibility, phasing out legacy checkout script tags in favor of Web Pixels.",
    whyItMatters: "Custom conversion tracking scripts embedded in legacy template files will cease firing, causing data loss in analytics and ad platforms.",
    recommendedAction: "Migrate any custom tracking tags or marketing pixels to official Shopify Apps or custom Web Pixels within Shopify Admin settings.",
    publishedAt: "2026-08-14",
    dateFormatted: "Aug 14, 2026",
    sourceLabel: "Shopify Changelog",
    sourceUrl: "https://shopify.dev/changelog",
    importance: "important",
    iconKey: "shopify",
  },
  {
    id: "meesho-return-rto-policy-rationalization",
    platform: "Meesho",
    platformKey: "meesho",
    category: "Marketplace",
    title: "Automated Return Fraud Shield & RTO Reverse Shipping Rationalization",
    summary: "Meesho rolled out machine-learning-driven customer return verification, preventing zero-rating claims and subsidizing reverse logistics on identified abusive return patterns.",
    whyItMatters: "D2C brands and volume apparel sellers should experience reduced RTO leakage and fairer settlement protection on high-frequency return items.",
    recommendedAction: "Review weekly RTO dispute records in Meesho Supplier Panel to verify that automated subsidies have been credited correctly.",
    publishedAt: "2026-08-08",
    dateFormatted: "Aug 08, 2026",
    sourceLabel: "Meesho Supplier Hub",
    sourceUrl: "https://supplier.meesho.com",
    importance: "fyi",
    iconKey: "meesho",
  },
  {
    id: "linkedin-b2b-lead-gen-ctas-2026",
    platform: "LinkedIn",
    platformKey: "linkedin",
    category: "Social",
    title: "Interactive Lead Gen Forms Integrated with Sponsored Thought Leader Ads",
    summary: "LinkedIn expanded Thought Leader ad placements to support native one-click lead generation forms, enabling direct conversion capture on executive member posts.",
    whyItMatters: "B2B brands can now combine founder personal branding with high-intent lead capture without sending prospects off-platform.",
    recommendedAction: "Test Thought Leader ads featuring founder perspective posts paired with a gated industry whitepaper or strategy session booking form.",
    publishedAt: "2026-08-02",
    dateFormatted: "Aug 02, 2026",
    sourceLabel: "LinkedIn Marketing Solutions",
    sourceUrl: "https://www.linkedin.com/business/marketing/blog",
    importance: "important",
    iconKey: "linkedin",
  },
  {
    id: "whatsapp-business-marketing-template-tiering",
    platform: "WhatsApp Business",
    platformKey: "meta",
    category: "Policy",
    title: "Meta Introduces Dynamic Per-User Frequency Caps on Marketing Conversations",
    summary: "Meta enacted automated frequency caps on promotional WhatsApp messages to prevent user notification fatigue, prioritizing conversational utility over bulk promotional blasts.",
    whyItMatters: "Unsegmented broadcast campaigns may experience message delivery throttling and degraded template quality scores.",
    recommendedAction: "Shift from batch broadcast campaigns to behavioral triggered notifications such as abandoned carts, reorders, and customer support follow-ups.",
    publishedAt: "2026-07-28",
    dateFormatted: "Jul 28, 2026",
    sourceLabel: "WhatsApp Business Help",
    sourceUrl: "https://business.whatsapp.com/blog",
    importance: "important",
    iconKey: "whatsapp",
  },
  {
    id: "google-merchant-center-next-feed-rules",
    platform: "Google Merchant Center",
    platformKey: "google",
    category: "Ecommerce",
    title: "Automated Product Studio Imagery Verification for Shopping Ads",
    summary: "Google Merchant Center enhanced feed processing to inspect AI-generated product backgrounds, establishing clarity and background compliance standards for sponsored listings.",
    whyItMatters: "Shopping ads featuring obscured packaging or misleading lifestyle backgrounds may trigger feed warnings or reduced auction impressions.",
    recommendedAction: "Inspect Merchant Center Diagnostics tab to ensure product main packshots feature clean contrast and comply with white-background requirements.",
    publishedAt: "2026-07-20",
    dateFormatted: "Jul 20, 2026",
    sourceLabel: "Google Merchant Center Help",
    sourceUrl: "https://support.google.com/merchants",
    importance: "fyi",
    iconKey: "google",
  },
];

/**
 * Filter categories for archive view
 */
export const platformUpdateCategories = [
  { label: "All", value: "all" },
  { label: "Google", value: "google" },
  { label: "Meta", value: "meta" },
  { label: "Amazon", value: "amazon" },
  { label: "Marketplaces", value: "marketplace" },
  { label: "SEO", value: "SEO" },
  { label: "Advertising", value: "advertising" },
  { label: "Social", value: "social" },
  { label: "Ecommerce", value: "ecommerce" },
  { label: "Policy", value: "policy" },
];

/**
 * Helper to get the newest platform updates, sorted chronologically descending.
 * @param {number} count Number of updates to retrieve (default: 3)
 */
export const getLatestPlatformUpdates = (count = 3) => {
  return [...platformUpdates]
    .sort((a, b) => new Date(b.publishedAt) - new Date(a.publishedAt))
    .slice(0, count);
};
